import { LIFE_SCIENCES_SERVICE_BASE_PATH } from '@my-backoffice/app/pages/life-sciences/shared/constants/life-sciences-api.constants';
import { IStudySystemLogPage } from '@my-backoffice/app/pages/life-sciences/shared/interfaces/study-system-log-page.interface';
import { IStudySystemLogTableParams } from '@my-backoffice/app/pages/life-sciences/shared/interfaces/study-system-log-table-params.interface';
import { IStudySystemLog } from '@my-backoffice/app/pages/life-sciences/shared/interfaces/study-system-log.interface';
import { SERVER_URL } from '@my-backoffice/app/shared/constants/app.constants';
import { HttpService } from '@my-backoffice/app/shared/services/http-services/http.service';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface IStudySystemLogApiItem {
  readonly historyActions: string;
  readonly historyType: string;
  readonly historyWhen: string;
  readonly historyWho: string;
  readonly id: string;
  readonly remark: null | string;
  readonly source: string;
  readonly targetId: string;
}

interface IStudySystemLogPageApiResponse {
  readonly countByFields: null | Record<string, unknown>;
  readonly pageItems: ReadonlyArray<IStudySystemLogApiItem>;
  readonly pageNo: number;
  readonly totalPageNum: number;
  readonly totalSize: number;
}

/**
 * @description
 * Maps a raw history-log item into UI system-log model.
 * @param {IStudySystemLogApiItem} item - Raw API item.
 * @returns {IStudySystemLog} Normalized UI system log item.
 */
function mapApiItemToStudySystemLog(item: IStudySystemLogApiItem): IStudySystemLog {
  return {
    id: item.id,
    studyId: item.targetId,
    userActor: item.historyWho,
    action: item.historyActions,
    details: item.remark ?? '',
    createTime: item.historyWhen,
  };
}

/**
 * @description
 * Maps history-log page payload into UI table page model.
 * @param {IStudySystemLogPageApiResponse} payload - API page payload.
 * @returns {IStudySystemLogPage} Normalized page model.
 */
function mapApiPageToStudySystemLogPage(payload: IStudySystemLogPageApiResponse): IStudySystemLogPage {
  const { pageItems, pageNo, totalPageNum, totalSize } = payload;
  const mappedPageItems = pageItems.map((item: IStudySystemLogApiItem): IStudySystemLog => mapApiItemToStudySystemLog(item));

  return {
    pageItems: mappedPageItems,
    pageNo,
    totalPageNum,
    totalSize,
  };
}

@Injectable({
  providedIn: 'root',
})
export class LifeSciencesStudySystemLogService {
  private readonly _httpService = inject(HttpService);

  /**
   * @description
   * Builds the paginated system logs API URL for qsp-data-table.
   * @param {Readonly<IStudySystemLogTableParams>} params - Table query parameters.
   * @returns {string} System logs list endpoint URL with query string.
   */
  public getTableUrl(params: Readonly<IStudySystemLogTableParams>): string {
    const { studyId, pageNo, pageSize } = params;
    const url = `${SERVER_URL}${LIFE_SCIENCES_SERVICE_BASE_PATH}/v1.0/history-log`;
    const searchParams = new URLSearchParams({
      targetId: studyId,
      pageNumber: pageNo.toString(),
      pageSize: pageSize.toString(),
    }).toString();

    return `${url}?${searchParams}`;
  }

  /**
   * @description
   * Returns recent system logs for the sidebar card preview.
   * @param {string} studyId - Study identifier.
   * @param {number} limit - Maximum number of preview entries.
   * @returns {Observable<IStudySystemLog[]>} Recent log entries.
   */
  public getRecentSystemLogs$(studyId: string, limit: number): Observable<readonly IStudySystemLog[]> {
    return this.getSystemLogsPage$({
      studyId,
      pageNo: 1,
      pageSize: limit,
    }).pipe(map((page): readonly IStudySystemLog[] => page.pageItems.slice(0, limit)));
  }

  /**
   * @description
   * Returns a paginated system log page from history-log API.
   * @param {IStudySystemLogTableParams} params - Pagination parameters.
   * @returns {Observable<IStudySystemLogPage>} Paginated system log list.
   */
  public getSystemLogsPage$(params: IStudySystemLogTableParams): Observable<IStudySystemLogPage> {
    const url = this.getTableUrl(params);

    return this._httpService.httpClient
      .get<IStudySystemLogPageApiResponse>(url)
      .pipe(map((response: IStudySystemLogPageApiResponse): IStudySystemLogPage => mapApiPageToStudySystemLogPage(response)));
  }
}
